var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useQuerySubscription } from '../../../hooks/useQuerySubscription';
import { getStoredAiToken, setStoredAiToken } from '../services/aiTokenStorage';
import { requestAiToken } from '../services/aiTokenApi';
import { UNDO_SETTLED_SSE_EVENT } from '../../../enums/AIBlocks';
import { NUDGE_SSE_EVENT } from '../constants/nudge';
import { ProgressThoughtCoalescer } from '../helpers/thoughtProgressCoalesce';
import { buildUndoData, parseUndoSettledTokens, settleUndoInBlocks } from '../helpers/streamHelpers';
import { getOrchestratorBaseUrl } from '../services/orchestrator-api-client';
// ---------------------------------------------------------------------------
// SSE Stream URL — GET /api/chat/{conversation_id}/stream (orchestrator, JWT auth)
// Auth is handled via Authorization header in useQuerySubscription, not query params.
// ---------------------------------------------------------------------------
export function getStreamUrl(conversationId) {
    return `${getOrchestratorBaseUrl()}/chat/stream/${conversationId}`;
}
const INITIAL_STATE = {
    responseId: null,
    conversationId: null,
    turnNumber: null,
    agent: null,
    blocks: [],
    streamingBlockId: null,
    streamingBlockType: null,
    streamingContent: '',
    isStreaming: false,
    responseState: 'idle',
    suggestedActions: [],
    durationMs: 0,
    feedbackId: null,
    pendingApproval: null,
    progressPhase: null,
    progressThoughtKey: null,
    progressMessage: null,
    progressDetail: null,
    progressAgent: null,
    pendingNudge: null
};
/**
 * Exported for tests ONLY so they can drive the REAL reducer map.
 *
 * `__test-utils__/mockSSEStream.buildReducers` keeps a hand-written copy of
 * these reducers, which means a production handler can go missing without any
 * test noticing — that is exactly how the `agent_resolved` badge bug survived
 * (the event was emitted by the orchestrator, absent from `SSEEventType`, and
 * unhandled here, yet every reducer test stayed green against the copy).
 * `useBlockStream.agentBadge.test.ts` asserts against this map directly, and
 * pins the copy's event set against it so the two cannot silently diverge again.
 */
export function buildEventHandlers(onStreamEnd, fallbackConversationId, progressCtx, onUndoSettled) {
    const { coalescer, queryClient, queryKey } = progressCtx;
    const patchProgressCache = (updater) => {
        queryClient.setQueryData(queryKey, (qprev) => updater(qprev !== null && qprev !== void 0 ? qprev : INITIAL_STATE));
    };
    return {
        stream_start: (payload, prev) => {
            coalescer.reset();
            const p = payload;
            return Object.assign(Object.assign({}, (prev || INITIAL_STATE)), { responseId: p.response_id, conversationId: p.conversation_id, turnNumber: p.turn_number, agent: p.agent, blocks: [], streamingBlockId: null, streamingBlockType: null, streamingContent: '', isStreaming: true, responseState: 'receiving', suggestedActions: [], durationMs: 0, feedbackId: null, pendingApproval: null, progressPhase: null, progressThoughtKey: null, progressMessage: null, progressDetail: null, progressAgent: null, 
                // A new turn starting supersedes any nudge left over from the
                // previous turn — never carry a stale nudge into a new response.
                pendingNudge: null });
        },
        /**
         * Correct the agent badge mid-stream.
         *
         * `stream_start` fires before classification completes, so its agent is the
         * "unknown" sentinel whose display_name is the generic "Assistant". The
         * orchestrator emits `agent_resolved` the moment classify_intent picks the
         * real agent — but until 2026-07-30 this event was unhandled here and absent
         * from `SSEEventType`, so it was dropped and the badge read "Assistant" for
         * the whole turn, for every agent. `stream_end` also carries `agent` and
         * likewise ignored it, so the badge never corrected at all.
         *
         * Touches ONLY `agent`: this event can arrive between block deltas, so
         * spreading a fresh INITIAL_STATE or resetting blocks here would drop
         * in-flight content.
         */
        agent_resolved: (payload, prev) => {
            const p = payload;
            const state = prev || INITIAL_STATE;
            if (!(p === null || p === void 0 ? void 0 : p.agent))
                return state;
            return Object.assign(Object.assign({}, state), { agent: p.agent });
        },
        block_start: (payload, prev) => {
            var _a;
            const p = payload;
            const state = prev || INITIAL_STATE;
            // For tool_progress blocks, create a live entry in blocks[] immediately
            if (p.block_type === 'tool_progress') {
                const liveBlock = {
                    type: 'tool_progress',
                    block_id: p.block_id,
                    block_index: (_a = p.block_index) !== null && _a !== void 0 ? _a : state.blocks.length,
                    agent: state.agent || {
                        name: 'office',
                        display_name: 'Assistant',
                        color: '#585870',
                        icon: 'fa-light fa-message-bot',
                        short_description: ''
                    },
                    task_label: 'Working\u2026',
                    steps: [],
                    is_complete: false
                };
                return Object.assign(Object.assign({}, state), { streamingBlockId: p.block_id, streamingBlockType: p.block_type, streamingContent: '', blocks: [...state.blocks, liveBlock] });
            }
            return Object.assign(Object.assign({}, state), { streamingBlockId: p.block_id, streamingBlockType: p.block_type, streamingContent: '' });
        },
        block_delta: (payload, prev) => {
            const p = payload;
            const state = prev || INITIAL_STATE;
            const { delta } = p;
            if (delta.kind === 'text') {
                return Object.assign(Object.assign({}, state), { streamingContent: state.streamingContent + delta.text });
            }
            if (delta.kind === 'step') {
                // Upsert the step into the live ToolProgressBlock in blocks[]
                const liveBlockIndex = state.blocks.findIndex((b) => b.block_id === state.streamingBlockId && b.type === 'tool_progress');
                if (liveBlockIndex >= 0) {
                    const liveBlock = state.blocks[liveBlockIndex];
                    const existingStepIndex = liveBlock.steps.findIndex((s) => s.step_id === delta.step.step_id);
                    const updatedSteps = existingStepIndex >= 0
                        ? liveBlock.steps.map((s, i) => (i === existingStepIndex ? delta.step : s))
                        : [...liveBlock.steps, delta.step];
                    const updatedBlock = Object.assign(Object.assign({}, liveBlock), { steps: updatedSteps });
                    const updatedBlocks = state.blocks.map((b, i) => (i === liveBlockIndex ? updatedBlock : b));
                    return Object.assign(Object.assign({}, state), { blocks: updatedBlocks });
                }
                return state;
            }
            return state;
        },
        block_end: (payload, prev) => {
            const state = prev || INITIAL_STATE;
            if (!state.streamingBlockId)
                return state;
            if (!state.streamingBlockType) {
                console.warn('[useBlockStream] block_end received without streamingBlockType — skipping block creation');
                return Object.assign(Object.assign({}, state), { streamingBlockId: null, streamingBlockType: null, streamingContent: '' });
            }
            const finalizedBlock = {
                type: state.streamingBlockType,
                block_id: state.streamingBlockId,
                block_index: state.blocks.length,
                content: state.streamingContent
            };
            return Object.assign(Object.assign({}, state), { blocks: [...state.blocks, finalizedBlock], streamingBlockId: null, streamingBlockType: null, streamingContent: '' });
        },
        block_complete: (payload, prev) => {
            const p = payload;
            const state = prev || INITIAL_STATE;
            // H.1: action_bar is merged into the preceding block, not rendered standalone
            if (p.block.type === 'action_bar') {
                if (state.blocks.length > 0) {
                    const lastIdx = state.blocks.length - 1;
                    const lastBlock = state.blocks[lastIdx];
                    const updatedBlock = Object.assign(Object.assign({}, lastBlock), { footerActions: p.block.actions });
                    const updatedBlocks = state.blocks.map((b, i) => (i === lastIdx ? updatedBlock : b));
                    return Object.assign(Object.assign({}, state), { blocks: updatedBlocks, streamingBlockId: null, streamingBlockType: null, streamingContent: '' });
                }
                // No preceding block — discard silently
                return Object.assign(Object.assign({}, state), { streamingBlockId: null, streamingBlockType: null, streamingContent: '' });
            }
            // H.2: undo is merged into the preceding block (same mechanism as action_bar)
            if (p.block.type === 'undo') {
                if (state.blocks.length > 0) {
                    const lastIdx = state.blocks.length - 1;
                    const lastBlock = state.blocks[lastIdx];
                    const updatedBlock = Object.assign(Object.assign({}, lastBlock), { 
                        // IP-15624: shared with normalizeHistoryMetadataBlocks. Both paths
                        // used to spell this object out by hand, so a field added to one was
                        // silently dropped by the other.
                        undoData: buildUndoData(p.block) });
                    const updatedBlocks = state.blocks.map((b, i) => (i === lastIdx ? updatedBlock : b));
                    return Object.assign(Object.assign({}, state), { blocks: updatedBlocks, streamingBlockId: null, streamingBlockType: null, streamingContent: '' });
                }
                // No preceding block — discard silently
                return Object.assign(Object.assign({}, state), { streamingBlockId: null, streamingBlockType: null, streamingContent: '' });
            }
            // Replace existing block with same block_id (block_end may have already added it),
            // or append if new. This prevents duplicates when both block_end and block_complete fire.
            const existingIndex = state.blocks.findIndex((b) => b.block_id === p.block.block_id);
            // If stream_start was missed (race condition when SSE connects after orchestrator
            // already started), infer streaming state so blocks still render.
            const inferredFields = state.responseState === 'idle'
                ? {
                    isStreaming: true,
                    responseState: 'receiving',
                    conversationId: state.conversationId || fallbackConversationId || null
                }
                : {};
            if (existingIndex >= 0) {
                const updatedBlocks = state.blocks.map((b, i) => (i === existingIndex ? p.block : b));
                return Object.assign(Object.assign(Object.assign({}, state), inferredFields), { blocks: updatedBlocks, streamingBlockId: null, streamingBlockType: null, streamingContent: '' });
            }
            return Object.assign(Object.assign(Object.assign({}, state), inferredFields), { blocks: [...state.blocks, p.block], streamingBlockId: null, streamingBlockType: null, streamingContent: '' });
        },
        approval_required: (payload, prev) => {
            const p = payload;
            const state = prev || INITIAL_STATE;
            return Object.assign(Object.assign({}, state), { pendingApproval: p, responseState: 'waiting_approval', blocks: [...state.blocks, p.confirmation] });
        },
        progress: (payload, prev) => {
            const p = payload;
            const state = prev || INITIAL_STATE;
            return coalescer.consume(state, p, patchProgressCache);
        },
        plan_progress: (payload, prev) => {
            const p = payload;
            const state = prev || INITIAL_STATE;
            const blockId = `plan-progress-${p.plan_id}-${p.step_index}`;
            // Check for existing block first so we can preserve its block_index
            const existingIdx = state.blocks.findIndex((b) => b.block_id === blockId);
            const progressBlock = {
                type: 'plan_progress',
                block_id: blockId,
                block_index: existingIdx >= 0 ? state.blocks[existingIdx].block_index : state.blocks.length,
                plan_id: p.plan_id,
                step_index: p.step_index,
                total_steps: p.total_steps,
                step_summary: p.step_summary,
                step_status: p.step_status,
                error_message: p.error_message
            };
            // Replace existing progress block for this step, or append
            if (existingIdx >= 0) {
                const updatedBlocks = state.blocks.map((b, i) => (i === existingIdx ? progressBlock : b));
                return Object.assign(Object.assign({}, state), { blocks: updatedBlocks });
            }
            return Object.assign(Object.assign({}, state), { blocks: [...state.blocks, progressBlock] });
        },
        // An undo reversed server-side this turn — typically because the user typed
        // "undo that" instead of clicking the card. The click path settles its own
        // button; the typed path had no client-side signal at all, so the card sat
        // there live over an operation that had just been reverted (IP-15624).
        //
        // Most of the work happens in the message cache via onUndoSettled: the card
        // being settled belongs to an earlier turn. Settling this turn's blocks too
        // covers the case where the card and the reversal share a turn, and costs a
        // no-op (same array reference back) when they don't.
        [UNDO_SETTLED_SSE_EVENT]: (payload, prev) => {
            const state = prev || INITIAL_STATE;
            const settledTokens = parseUndoSettledTokens(payload);
            if (settledTokens.size === 0)
                return state;
            onUndoSettled === null || onUndoSettled === void 0 ? void 0 : onUndoSettled(settledTokens);
            const blocks = settleUndoInBlocks(state.blocks, settledTokens);
            return blocks === state.blocks ? state : Object.assign(Object.assign({}, state), { blocks });
        },
        // New-conversation nudge (ipa-training-mode). Cross-repo Nudge Contract
        // v1 — the orchestrator sends at most one per turn. Rendering (and the
        // training-mode gate) happens in AIChatWidget/NudgeBanner; this handler
        // only stores the payload. A stray nudge event when training mode is off
        // is harmless — it's simply never rendered (fail-safe by construction).
        [NUDGE_SSE_EVENT]: (payload, prev) => {
            const state = prev || INITIAL_STATE;
            return Object.assign(Object.assign({}, state), { pendingNudge: payload });
        },
        stream_end: (payload, prev) => {
            var _a;
            coalescer.reset();
            const p = payload;
            const state = prev || INITIAL_STATE;
            const final = Object.assign(Object.assign({}, state), { responseId: state.responseId || p.response_id || null, conversationId: state.conversationId || fallbackConversationId || null, 
                // Backstop for the badge: on a reconnect that missed agent_resolved,
                // stream_end is the only remaining carrier of the real agent. Prefer the
                // payload, but never regress a resolved badge back to null.
                agent: p.agent || state.agent, turnNumber: (_a = p.turn_number) !== null && _a !== void 0 ? _a : state.turnNumber, isStreaming: false, responseState: 'complete', suggestedActions: p.suggested_actions || [], durationMs: p.duration_ms, feedbackId: p.feedback_id, pendingApproval: null, progressPhase: null, progressThoughtKey: null, progressMessage: null, progressDetail: null, progressAgent: null });
            onStreamEnd === null || onStreamEnd === void 0 ? void 0 : onStreamEnd(final);
            return final;
        }
    };
}
export function useBlockStream({ conversationId, enabled = true, onStreamEnd, onUndoSettled }) {
    const queryClient = useQueryClient();
    const queryKey = useMemo(() => ['ai-chat', 'stream', conversationId], [conversationId]);
    // Tracks the conversation currently in view so the async auth-refresh below
    // can detect a mid-flight conversation switch (see onAuthError).
    const conversationIdRef = useRef(conversationId);
    useEffect(() => {
        conversationIdRef.current = conversationId;
    }, [conversationId]);
    const progressCoalescerRef = useRef(new ProgressThoughtCoalescer());
    useEffect(() => {
        progressCoalescerRef.current.reset();
        return () => {
            progressCoalescerRef.current.reset();
        };
    }, [conversationId]);
    const url = conversationId ? getStreamUrl(conversationId) : '';
    const [tokenRefreshKey, setTokenRefreshKey] = useState(0);
    const headers = useMemo(() => {
        const token = getStoredAiToken();
        if (token) {
            return { Authorization: `Bearer ${token}` };
        }
        return {};
    }, [conversationId, tokenRefreshKey]);
    // Auth-refresh guard: one-shot 401 recovery.
    // authRefreshAttemptedRef prevents infinite loops: the first 401 triggers a
    // token refresh + reconnect; if the second attempt also 401s, we fall through
    // to the terminal error path and show the banner. The ref is reset on every
    // successful connection open (onOpen below).
    const authRefreshAttemptedRef = useRef(false);
    // The guard is per-conversation. Without this reset, a 401 on conversation A
    // leaves it set, and conversation B's FIRST 401 reads as a second
    // consecutive failure — B goes straight to the terminal "Connection lost"
    // banner without ever attempting its own refresh.
    const [wasAuthRefresh, setWasAuthRefresh] = useState(false);
    useEffect(() => {
        authRefreshAttemptedRef.current = false;
        setWasAuthRefresh(false);
    }, [conversationId]);
    // sseReconnectCallbackRef holds the inner reconnect() from useQuerySubscription
    // so onAuthError (defined before the hook call below) can call it without a
    // circular dependency.
    const sseReconnectCallbackRef = useRef(null);
    const onAuthError = useCallback((_err) => {
        if (authRefreshAttemptedRef.current) {
            // Second consecutive 401 — the Rails session itself is gone.
            // Signal terminal error; the banner will show "Connection lost."
            return false;
        }
        authRefreshAttemptedRef.current = true;
        setWasAuthRefresh(true);
        // Capture which conversation this 401 belongs to. If the user switches
        // conversations before the refresh resolves, sseReconnectCallbackRef will
        // have moved on to the new conversation's reconnect() — calling it here
        // would force an unwanted reconnect on a stream this recovery has nothing
        // to do with (see CodeRabbit finding on this hook).
        const authRefreshConversationId = conversationIdRef.current;
        // Mirrors existing reconnect() body: refresh token then re-open SSE.
        void (() => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                const response = yield requestAiToken();
                if (response.data) {
                    setStoredAiToken(response.data.token, response.data.expires_at);
                }
            }
            catch (_b) {
                // Proceed with existing token if the refresh call itself fails.
            }
            if (conversationIdRef.current !== authRefreshConversationId) {
                // Conversation changed mid-refresh — the stored token is still
                // updated for next use, but don't reconnect a stream that isn't ours.
                return;
            }
            flushSync(() => {
                setTokenRefreshKey((k) => k + 1);
            });
            (_a = sseReconnectCallbackRef.current) === null || _a === void 0 ? void 0 : _a.call(sseReconnectCallbackRef);
        }))();
        return true;
    }, []); // stable — all mutable state accessed via refs
    const onOpen = useCallback(() => {
        // Successful open: reset the one-shot guard so the next genuine 401 (e.g.
        // after a very long idle session) can trigger a fresh refresh attempt.
        authRefreshAttemptedRef.current = false;
        setWasAuthRefresh(false);
    }, []);
    const eventHandlers = useMemo(() => buildEventHandlers(onStreamEnd, conversationId, {
        coalescer: progressCoalescerRef.current,
        queryClient,
        queryKey
    }, onUndoSettled), [onStreamEnd, conversationId, queryClient, queryKey, onUndoSettled]);
    const { data, isConnected, error, abort, reconnect: sseReconnect, connectionState, reconnectAttempts } = useQuerySubscription({
        queryKey,
        url,
        enabled: enabled && Boolean(conversationId),
        headers,
        eventHandlers,
        initialData: INITIAL_STATE,
        onOpen,
        onAuthError
    });
    // Keep the ref current so the stable onAuthError callback can call the
    // latest sseReconnect without capturing a stale closure. Assigned in a
    // layout effect rather than during render: React can abandon a render, and
    // a ref written by uncommitted work would let auth recovery call a
    // reconnect() belonging to a tree that was never mounted.
    useLayoutEffect(() => {
        sseReconnectCallbackRef.current = sseReconnect;
    }, [sseReconnect]);
    const reset = useCallback(() => {
        progressCoalescerRef.current.reset();
        queryClient.setQueryData(queryKey, INITIAL_STATE);
    }, [queryClient, queryKey]);
    const clearNudge = useCallback(() => {
        queryClient.setQueryData(queryKey, (qprev) => (qprev ? Object.assign(Object.assign({}, qprev), { pendingNudge: null }) : qprev));
    }, [queryClient, queryKey]);
    const abortStream = useCallback(() => {
        progressCoalescerRef.current.reset();
        abort();
    }, [abort]);
    const reconnect = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield requestAiToken();
            if (response.data) {
                setStoredAiToken(response.data.token, response.data.expires_at);
            }
        }
        catch (_a) {
            // Proceed with the existing token if refresh fails
        }
        flushSync(() => {
            setTokenRefreshKey((k) => k + 1);
        });
        sseReconnect();
    }), [sseReconnect]);
    return {
        state: data || INITIAL_STATE,
        isConnected,
        error,
        abort: abortStream,
        reset,
        reconnect,
        connectionState,
        reconnectAttempts,
        clearNudge,
        wasAuthRefresh
    };
}
